import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ValueType } from 'react-select/src/types';
import {
  OBContact,
  OBContactExtraAttributes,
  OptionType,
} from '../../../types';
import {
  DARK_PLACEHOLDER,
  DISABLED_PLACEHOLDER,
  formatOptionLabel,
  getNewValue,
} from '../../../constants';
import { ONBOARDING_ACTIONS } from '../../../actions/onboarding';
import { apiRequest } from '../../../APIClient';
import InputDropdown from '../../../widgets/InputDropdown';
import {
  getContactOption,
  getPrimaryContactId,
  onboardingSelector,
} from '../../../reducers/onboarding';
import { chainApiDispatches, getDelta } from '../../../utils';

export type Props = {
  placeholder: string;
  contactAttr: keyof OBContactExtraAttributes;
  disabled?: boolean;
  id?: string;
  requireEmail?: boolean;
};

const noOptionsMessage = () => 'No available contacts.';

const optionToUuid = (option: OptionType) => option.value;

const OnboardingContactDropdown: React.FunctionComponent<Props> = ({
  placeholder,
  contactAttr,
  disabled,
  id,
  requireEmail,
}) => {
  const dispatch = useDispatch();
  const contacts = useSelector(onboardingSelector.getContacts);

  const [selectContacts, setSelectContacts] = React.useState<OptionType[]>([]);
  const dlOptionsAreFiltered = [
    'contactType',
    'isRiskUser',
    'isPortalUser',
    'isLogSearchUser',
    'isManagedAwarenessUser',
  ].includes(contactAttr);

  const mspContactsAreEnabled = [
    'isReportRecipient',
    'isBasicEscalationContact',
  ].includes(contactAttr);

  const contactOptions = React.useMemo(() => {
    // Shipping contacts cannot have attribute settings
    let contactEntries = Object.entries(contacts).filter(
      // DLs cannot be selected as Primary, Secondary and any of the Access controls
      ([, contact]) =>
        !((dlOptionsAreFiltered && contact.isDL) || contact.isShippingContact),
    );
    if (contactAttr === 'contactType') {
      // remove primary contact; a contact cannot be primary & secondary at the same time
      contactEntries = contactEntries.filter(
        ([, contact]) => contact.contactType !== 'primary',
      );
    }
    return contactEntries.map(([uuid, contact]) =>
      getContactOption(uuid, contact, !mspContactsAreEnabled, requireEmail),
    );
  }, [
    contacts,
    contactAttr,
    dlOptionsAreFiltered,
    mspContactsAreEnabled,
    requireEmail,
  ]);

  // keep selection up to date with the contacts in store
  React.useEffect(() => {
    let contactEntries = Object.entries(contacts);
    if (contactAttr !== 'contactType') {
      contactEntries = contactEntries.filter(
        ([, contact]) => contact[contactAttr],
      );
    } else {
      contactEntries = contactEntries.filter(
        ([, contact]) =>
          contact.contactType ===
          (id === 'selectSecondaryContact' ? 'secondary' : 'primary'),
      );
    }
    setSelectContacts(
      contactEntries.map(([uuid, contact]) => getContactOption(uuid, contact)),
    );
  }, [setSelectContacts, contacts, contactAttr, id]);

  const handleMultiSelectChange = React.useCallback(
    (value: ValueType<OptionType>) => {
      const v = getNewValue(value, true) || [];
      const { delta, isAdd } = getDelta(
        selectContacts.map(optionToUuid),
        v.map(optionToUuid),
      );
      delta.forEach((uuid) => {
        const updatedContact = { ...contacts[uuid] };
        if (!isAdd) {
          if (contactAttr !== 'contactType') {
            updatedContact[contactAttr] = false;
          } else {
            updatedContact[contactAttr] = 'normal';
          }
        } else {
          if (contactAttr !== 'contactType') {
            updatedContact[contactAttr] = true;
          } else {
            updatedContact[contactAttr] = 'secondary';
          }
        }
        dispatch(ONBOARDING_ACTIONS.SET_CONTACT(uuid, updatedContact));
        dispatch(apiRequest.updateOBContact(uuid, updatedContact));
      });
    },
    [dispatch, contacts, selectContacts, contactAttr],
  );

  const handlePrimaryContactChange = React.useCallback(
    (value: ValueType<OptionType>) => {
      const v = getNewValue(value, false);
      if (v) {
        const updatedContact = { ...contacts[v.value] };
        updatedContact.contactType = 'primary';
        const primaryContactId = getPrimaryContactId(contacts);
        if (primaryContactId && v.value !== primaryContactId) {
          // Reassign previous primary contact to normal because we only allow one primary contact
          const changedPrimary: OBContact = {
            ...contacts[primaryContactId],
            contactType: 'normal',
          };
          dispatch(
            ONBOARDING_ACTIONS.SET_CONTACT(primaryContactId, changedPrimary),
          );
          dispatch(ONBOARDING_ACTIONS.SET_CONTACT(v.value, updatedContact));
          chainApiDispatches(dispatch, [
            apiRequest.updateOBContact(primaryContactId, changedPrimary),
            apiRequest.updateOBContact(v.value, updatedContact),
          ]);
        } else {
          dispatch(ONBOARDING_ACTIONS.SET_CONTACT(v.value, updatedContact));
          dispatch(apiRequest.updateOBContact(v.value, updatedContact));
        }
      }
    },
    [contacts, dispatch],
  );

  return (
    <InputDropdown
      // creates a portal to render the menu on the body rather than the default which is the right beneath the input container
      // this solves the z-index issue when it is in a accordion
      menuPortalTarget={document.body}
      isCreatable={false}
      isMulti={id !== 'selectPrimaryContact'}
      noOptionsMessage={noOptionsMessage}
      showDropdownArrow={true}
      onChange={(value) =>
        id !== 'selectPrimaryContact'
          ? handleMultiSelectChange(value)
          : handlePrimaryContactChange(value)
      }
      options={contactOptions}
      placeholder={placeholder}
      styles={mspContactsAreEnabled ? DARK_PLACEHOLDER : DISABLED_PLACEHOLDER}
      value={selectContacts}
      isDisabled={disabled}
      id={id}
      classNamePrefix={id}
      // disable "x" button to clear all so UI action only
      // results in changes on a single contact
      isClearable={false}
      formatOptionLabel={formatOptionLabel}
    />
  );
};

export default OnboardingContactDropdown;
